import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { StyleClassModule } from 'primeng/styleclass';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Avatar } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '@/app/layout/service/layout.service';
import { AppNotifications } from './app.notifications';
import { AppProfile } from './app.profile';
import { AuthService } from '@/app/pages/service/auth/auth.service';
import { AppCompany } from './app.company';
import { OverlayBadgeModule } from 'primeng/overlaybadge';

@Component({
    selector: '[app-header]',
    standalone: true,
    imports: [CommonModule, FormsModule, StyleClassModule, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, Avatar, TooltipModule, RouterLink, AppNotifications, AppProfile, AppCompany, OverlayBadgeModule],
    template: `
        <div class="topbar-left">
            @if (showMenuButton()) {
                <button type="button" (click)="layoutService.toggleMenu()" class="menu-button">
                    <i class="pi pi-bars"></i>
                </button>
                <!-- <span class="topbar-separator"></span> -->
            }
            <!-- <div class="page-title">
                {{ pageTitle() }}
            </div> -->
        </div>

        <div class="topbar-right">
            <!-- <p-iconfield>
                <p-inputicon class="pi pi-search" />
                <input type="text" pInputText [(ngModel)]="search" placeholder="Search" />
            </p-iconfield> -->

            <div class="topbar-actions">
                <button type="button" (click)="layoutService.toggleMenu()" class="menu-button menu-button-mobile">
                    <i class="pi pi-bars"></i>
                </button>

                <!-- <button type="button" (click)="layoutService.toggleConfigSidebar()" class="app-config-button">
                    <i class="pi pi-cog"></i>
                </button> -->

                <div class="relative">
                    <p-button icon="pi pi-sparkles" severity="secondary" [outlined]="true" routerLink="/ai-assistant"
                        pTooltip="Assistente IA HubyLog" tooltipPosition="bottom" />
                </div>

                <div class="relative">
                    <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="p-anchored-overlay-enter-active" leaveActiveClass="p-anchored-overlay-leave-active" leaveToClass="hidden" [hideOnOutsideClick]="true">
                        @if (totalNotificacoes() > 0) {
                            <p-overlaybadge [value]="totalNotificacoes()" severity="info" styleClass="animate-pulse" badgeSize="small">
                                <p-button icon="pi pi-bell" severity="secondary" [outlined]="true" />
                            </p-overlaybadge>
                        } @else {
                            <p-button icon="pi pi-bell" severity="secondary" [outlined]="true" />
                        }
                    </a>
                    <div class="absolute hidden min-w-72 top-auto right-0 z-20 mt-2">
                        <div app-notifications styleClass="w-full sm:w-[22rem]"></div>
                    </div>
                </div>

                <div class="relative">
                    <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="p-anchored-overlay-enter-active" leaveActiveClass="p-anchored-overlay-leave-active" leaveToClass="hidden" [hideOnOutsideClick]="true">
                        <p-button icon="pi pi-building" severity="secondary" [outlined]="true" />
                    </a>
                    <div class="absolute hidden min-w-72 top-auto right-0 z-20 mt-2">
                        <div app-company styleClass="w-full sm:w-[22rem]"></div>
                    </div>
                </div>

                <div class="relative">
                    <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="p-anchored-overlay-enter-active" leaveActiveClass="p-anchored-overlay-leave-active" leaveToClass="hidden" [hideOnOutsideClick]="true">
                        <p-button 
                            [icon]="layoutService.isDarkTheme() ? 'pi pi-moon' : 'pi pi-sun'"
                            severity="secondary" 
                            [outlined]="true"
                            (click)="executeDarkModeToggle()"
                            [styleClass]="spinning ? 'spin-icon' : ''"
                        />
                    </a>
                </div>

                <div class="relative">
                    <a pStyleClass="@next" enterFromClass="hidden" enterActiveClass="p-anchored-overlay-enter-active" leaveActiveClass="p-anchored-overlay-leave-active" leaveToClass="hidden" [hideOnOutsideClick]="true" class="flex items-center">
                        @if (user?.foto) {
                            <p-avatar 
                                [image]="user.foto"
                                styleClass="!rounded-md !overflow-hidden !w-9 !h-9 cursor-pointer" 
                                />
                        } @else {
                            <p-avatar 
                                [image]="'/assets/images/logo-marca-usuario.png'"
                                styleClass="!rounded-md !overflow-hidden !w-9 !h-9 cursor-pointer" 
                                />
                        }
                    </a>
                    <div class="absolute hidden top-full right-0 mt-2 z-20">
                        <div app-profile class="w-52"></div>
                    </div>
                </div>

                <!-- <p-button (click)="layoutService.toggleRightMenu()" icon="pi pi-align-right" severity="secondary" [outlined]="true" />
                <div app-rightmenu></div> -->
            </div>
        </div>
    `
})
export class AppHeader {
    layoutService = inject(LayoutService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

    user: any = {};

    spinning = false;

    search = signal('');

    private notificacoes = viewChild(AppNotifications);

    totalNotificacoes = computed(() => this.notificacoes()?.totalNotificacoes() ?? 0);

    showMenuButton = computed(() => {
        const menuMode = this.layoutService.layoutConfig().menuMode;
        return menuMode === 'overlay' || menuMode === 'static';
    });

    private pageTitle$ = this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => {
            let route = this.activatedRoute;
            while (route.firstChild) {
                route = route.firstChild;
            }
            return route.snapshot.data['title'] || 'Dashboard';
        })
    );

    pageTitle = toSignal(this.pageTitle$, { initialValue: 'Dashboard' });

    constructor(
        private _authService: AuthService,
    ) {}
        
    ngOnInit() {
        this.getUser();
    }

    getUser() {
        this.user = this._authService.getUserLogged().result;
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => {
            localStorage.setItem('theme', !state.darkTheme ? 'dark' : 'light');
            return {
                ...state,
                darkTheme: !state.darkTheme
           }  
        });

        this.layoutService.toggleDarkMode();
    }

    executeDarkModeToggle() {
        this.spinning = true;

        this.layoutService.layoutConfig.update((state) => {
            const newDarkTheme = !state.darkTheme;
            const newTheme = newDarkTheme ? 'dark' : 'light';

            localStorage.setItem('theme', newTheme);

            return {
                ...state,
                darkTheme: newDarkTheme,
                menuTheme: newTheme,
                topbarTheme: newTheme
            };
        });

        setTimeout(() => {
            this.spinning = false;
        }, 400);
    }
}
